import React, { useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Flex,
  Input,
  Space as AntSpace,
  Segmented,
  Space,
  Tooltip,
  Typography,
  theme as antdTheme,
} from "antd";
import {
  BellOutlined,
  MoonOutlined,
  SearchOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SunOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useGetIdentity, useNavigation, useLogout } from "@refinedev/core";
import type { ThemeMode } from "../../theme";

type AdminIdentity = {
  id: string;
  name?: string | null;
  avatar?: string | null;
};

type AdminHeaderProps = {
  themeMode: ThemeMode;
  onThemeChange: (mode: ThemeMode) => void;
};

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  themeMode,
  onThemeChange,
}) => {
  const { token } = antdTheme.useToken();
  const { data: identity } = useGetIdentity<AdminIdentity>();
  const { push, create } = useNavigation();
  const { mutate: logout } = useLogout();
  const [searchValue, setSearchValue] = useState("");

  const initials = identity?.name?.[0]?.toUpperCase() ?? "A";

  const menuItems: MenuProps["items"] = [
    {
      key: "profile",
      label: "账户信息",
      disabled: true,
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      label: "安全退出",
    },
  ];

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    push(`/movies${trimmed ? `?search=${encodeURIComponent(trimmed)}` : ""}`);
  };

  const triggerSearch = () => handleSearch(searchValue);

  return (
    <Flex
      align="center"
      justify="space-between"
      style={{
        padding: "12px 20px",
        borderBottom: `1px solid ${token.colorSplit}`,
        background: token.colorBgContainer,
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      <Space direction="vertical" size={2}>
        <Typography.Text type="secondary">
          夜光宝盒 · 运营工作台
        </Typography.Text>
        <Typography.Title
          level={5}
          style={{ margin: 0, color: token.colorText }}
        >
          今日要务：跟进重点影片与审核排期
        </Typography.Title>
      </Space>

      <Space size="large" align="center">
        <AntSpace.Compact style={{ width: 320 }}>
          <Input
            allowClear
            value={searchValue}
            placeholder="搜索电影 / 分类 / 任务"
            onChange={(event) => setSearchValue(event.target.value)}
            onPressEnter={triggerSearch}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={triggerSearch}
          >
            搜索
          </Button>
        </AntSpace.Compact>
        <Segmented
          value={themeMode}
          onChange={(value) => onThemeChange(value as ThemeMode)}
          options={[
            {
              label: "浅色",
              value: "light",
              icon: <SunOutlined />,
            },
            {
              label: "深色",
              value: "dark",
              icon: <MoonOutlined />,
            },
          ]}
        />
        <Tooltip title="查看设计规范">
          <Button
            type="text"
            shape="circle"
            icon={<QuestionCircleOutlined />}
            onClick={() =>
              window.open("https://pro.ant.design/zh-CN/docs/overview", "_blank")
            }
          />
        </Tooltip>
        <Tooltip title="通知中心">
          <Badge dot>
            <Button type="text" shape="circle" icon={<BellOutlined />} />
          </Badge>
        </Tooltip>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => create("movies")}
        >
          新建影片
        </Button>
        <Dropdown
          menu={{
            items: menuItems,
            onClick: ({ key }) => {
              if (key === "logout") {
                logout();
              }
            },
          }}
        >
          <Space align="center">
            <Avatar src={identity?.avatar}>{initials}</Avatar>
            <Typography.Text style={{ color: token.colorText }}>
              {identity?.name ?? "管理员"}
            </Typography.Text>
          </Space>
        </Dropdown>
      </Space>
    </Flex>
  );
};
